import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePayment } from '@/context/PaymentContext';
import { useAuth } from '@/context/AuthContext';
import { useCRM } from '@/hooks/useCRM';
import { useToast } from '@/hooks/use-toast';
import type { ContactStage } from '@/types/contacts';
import { 
  Phone, 
  Mail, 
  Linkedin, 
  Globe, 
  Building2, 
  MapPin, 
  DollarSign,
  Lock,
  CreditCard,
  X,
  Clock,
  Briefcase,
  Check
} from 'lucide-react';
import type { Investor } from '@/types';

interface ProfileDetailProps {
  investor: Investor | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originPosition?: { x: number; y: number } | null;
  onAddToCRM?: (investor: Investor) => void;
  isAddedToCRM?: boolean;
}

export function ProfileDetail({ investor, open, onOpenChange, originPosition, onAddToCRM, isAddedToCRM }: ProfileDetailProps) {
  const navigate = useNavigate();
  const { isSubscribed } = usePayment();
  const { isApproved, isAuthenticated } = useAuth();
  const { createContact } = useCRM();
  const { toast } = useToast();
  const modalRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const handleAddToCRM = async () => {
    if (!investor || isAddedToCRM) return;
    
    setIsAdding(true);
    try {
      if (onAddToCRM) {
        await onAddToCRM(investor);
      } else {
        // Direct workspace integration
        const contactData = {
          name: investor.name,
          email: investor.contactInfo || '',
          phone: investor.phone || '',
          company: investor.firm,
          title: investor.role || investor.investorType,
          stage: 'new' as ContactStage,
          source: 'RaiseBeacon Search',
          tags: [...investor.industries, investor.investorType].filter(Boolean),
          value: 0,
          location: investor.location,
          website: investor.website || '',
          linkedin: investor.linkedin || '',
          notes: '', // Notes are for user input only
        };
        await createContact(contactData);
      }
    } catch (error) {
      // Error handled by useCRM
    } finally {
      setIsAdding(false);
    }
  };

  useEffect(() => {
    if (open) {
      // Small delay to ensure animation triggers
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [open]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onOpenChange(false), 300);
  };

  const handleUnlock = () => {
    handleClose();
    navigate('/pricing');
  };

  if (!investor) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[110]"
          />

          {/* Modal Container - centers the modal */}
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            {/* The Magic Modal - expands from click position */}
            <motion.div
              ref={modalRef}
              initial={{
                opacity: 0,
                scale: 0.3,
                x: originPosition ? originPosition.x - window.innerWidth / 2 : 0,
                y: originPosition ? originPosition.y - window.innerHeight / 2 : 100,
              }}
              animate={{
                opacity: isVisible ? 1 : 0,
                scale: isVisible ? 1 : 0.3,
                x: 0,
                y: 0,
              }}
              exit={{
                opacity: 0,
                scale: 0.5,
                transition: { duration: 0.2 }
              }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300,
                duration: 0.4
              }}
              className="relative w-full max-w-lg bg-background rounded-3xl shadow-2xl overflow-hidden pointer-events-auto"
              style={{ transformOrigin: 'center center' }}
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 w-8 h-8 bg-background/20 hover:bg-background/30 backdrop-blur rounded-full flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>

              {/* Header with gradient */}
              <div className="bg-gradient-to-br from-[#2B7FFF] to-[#1A5FCC] p-8 text-white">
                <div className="pr-8">
                  <h2 className="text-2xl font-semibold text-white">
                    {isApproved && isSubscribed ? investor.name : 'Investor Profile'}
                  </h2>
                </div>
                
                {/* Investor Type Badge */}
                <div className="mt-4">
                  <Badge 
                    variant="secondary" 
                    className="bg-background/20 text-white border-0 rounded-full px-4 py-1 text-sm font-medium"
                  >
                    {isApproved && isSubscribed ? investor.investorType : 'Locked'}
                  </Badge>
                </div>
              </div>

              {/* Content */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                className="p-8 max-h-[60vh] overflow-y-auto"
              >
                {!isAuthenticated ? (
                  /* Not logged in state */
                  <div className="text-center py-8">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className="w-20 h-20 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-6"
                    >
                      <Lock className="w-10 h-10 text-muted-foreground" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Investor Details Locked
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
                      Subscribe to unlock full contact details and investor information.
                    </p>
                    <div className="flex flex-col gap-3">
                      <Button
                        onClick={handleUnlock}
                        className="bg-gradient-to-r from-[#2B7FFF] to-[#1A5FCC] hover:from-[#1A5FCC] hover:to-[#2B7FFF] text-white px-6"
                      >
                        <CreditCard className="w-5 h-5 mr-2" />
                        View Pricing
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          handleClose();
                          navigate('/login');
                        }}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        Or sign in
                      </Button>
                    </div>
                  </div>
                ) : !isApproved ? (
                  /* Pending approval state */
                  <div className="text-center py-8">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6"
                    >
                      <Clock className="w-10 h-10 text-amber-600" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Pending Approval
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
                      Your account needs to be approved by an administrator before you can view investor details.
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <p className="text-sm text-amber-700">
                        You'll be notified once your account is approved.
                      </p>
                    </div>
                  </div>
                ) : !isSubscribed ? (
                  /* Blurred Preview for non-subscribers */
                  <div className="text-center py-8">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className="w-20 h-20 bg-gradient-to-br from-[#2B7FFF]/10 to-[#1A5FCC]/10 rounded-2xl flex items-center justify-center mx-auto mb-6"
                    >
                      <Lock className="w-10 h-10 text-[#2B7FFF]" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      Full Access Required
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
                      Subscribe to unlock full contact details for {investor.name}
                    </p>
                    
                    {/* Blurred preview fields */}
                    <div className="space-y-3 mb-6 max-w-sm mx-auto">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-10 bg-muted rounded-xl blur-sm" />
                      ))}
                    </div>

                    <Button
                      onClick={handleUnlock}
                      className="bg-gradient-to-r from-[#2B7FFF] to-[#1A5FCC] hover:from-[#1A5FCC] hover:to-[#2B7FFF] text-white rounded-full px-8 py-6 text-base font-medium shadow-lg shadow-[#2B7FFF]/25 transition-all duration-300"
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      View Pricing
                    </Button>
                  </div>
                ) : (
                  /* Full content for subscribers */
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      {investor.firm !== 'Google Maps' && (
                        <InfoCard 
                          icon={Building2} 
                          label="Firm" 
                          value={investor.firm} 
                        />
                      )}
                      <InfoCard 
                        icon={MapPin} 
                        label="Location" 
                        value={investor.location} 
                      />
                    </div>

                    {/* Investment Focus */}
                    <div className="bg-muted rounded-2xl p-5">
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">Investment Focus</h4>
                      <div className="flex flex-wrap gap-2">
                        {investor.industries.map((industry, i) => (
                          <motion.div
                            key={industry}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.1 + i * 0.05 }}
                          >
                            <Badge 
                              variant="secondary"
                              className="bg-background text-foreground rounded-full px-3 py-1 text-sm font-normal shadow-sm"
                            >
                              {industry}
                            </Badge>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">Contact Information</h4>
                      
                      {investor.phone && (
                        <ContactRow 
                          icon={Phone}
                          label="Phone"
                          value={investor.phone}
                          href={`tel:${investor.phone}`}
                          delay={0.1}
                        />
                      )}
                      
                      {investor.contactInfo && (
                        <ContactRow 
                          icon={Mail}
                          label="Email"
                          value={investor.contactInfo}
                          href={`mailto:${investor.contactInfo}`}
                          delay={0.15}
                        />
                      )}
                      
                      {investor.linkedin && (
                        <ContactRow 
                          icon={Linkedin}
                          label="LinkedIn"
                          value="View Profile"
                          href={investor.linkedin}
                          external
                          delay={0.2}
                        />
                      )}
                      
                      {investor.website && (
                        <ContactRow 
                          icon={Globe}
                          label="Website"
                          value={investor.website.replace(/^https?:\/\//, '')}
                          href={investor.website}
                          external
                          delay={0.25}
                        />
                      )}
                    </div>

                    {/* Check Size */}
                    {investor.checkSize && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="flex items-center gap-3 text-muted-foreground bg-muted rounded-xl p-4"
                      >
                        <DollarSign className="w-5 h-5 text-[#2B7FFF]" />
                        <span className="text-sm">Typical investment: <strong className="text-foreground">{investor.checkSize}</strong></span>
                      </motion.div>
                    )}

                    {/* Save to network */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35 }}
                      className="pt-4 border-t border-slate-100"
                    >
                      <Button
                        onClick={handleAddToCRM}
                        disabled={isAddedToCRM || isAdding}
                        className={`w-full py-6 text-base font-medium rounded-xl transition-all ${
                          isAddedToCRM
                            ? 'bg-green-500 hover:bg-green-500 text-white'
                            : 'bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white'
                        }`}
                      >
                        {isAdding ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                            Adding...
                          </>
                        ) : isAddedToCRM ? (
                          <>
                            <Check className="w-5 h-5 mr-2" />
                            Saved to network
                          </>
                        ) : (
                          <>
                            <Briefcase className="w-5 h-5 mr-2" />
                            Save to network
                          </>
                        )}
                      </Button>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

function InfoCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="bg-muted rounded-2xl p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        <Icon className="w-4 h-4" />
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-foreground font-medium">{value}</p>
    </div>
  );
}

function ContactRow({ 
  icon: Icon, 
  label, 
  value, 
  href, 
  external,
  delay = 0
}: { 
  icon: React.ElementType; 
  label: string; 
  value: string; 
  href: string;
  external?: boolean;
  delay?: number;
}) {
  return (
    <motion.a
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.3 }}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="flex items-center gap-4 p-4 bg-muted rounded-2xl hover:bg-[#2B7FFF]/5 transition-colors group"
    >
      <div className="w-10 h-10 bg-background rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
        <Icon className="w-5 h-5 text-[#2B7FFF]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-foreground font-medium truncate">{value}</p>
      </div>
    </motion.a>
  );
}

